import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Upload, X, Plus } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/PageHeader";
import { Loader } from "../components/Loader";
import { fileToCompressedDataUrl } from "../lib/image";

export default function AddProduct() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(isEdit);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [discount, setDiscount] = useState("");
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [storeCategories, setStoreCategories] = useState([]);
  const [interests, setInterests] = useState([]);
  const [cities, setCities] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [storeCategoryId, setStoreCategoryId] = useState("");
  const [interestId, setInterestId] = useState("");
  const [openParent, setOpenParent] = useState("");
  const [deliveryType, setDeliveryType] = useState("");
  const [flatPrice, setFlatPrice] = useState("");
  const [cityPrices, setCityPrices] = useState({});

  useEffect(() => {
    (async () => {
      try { setCategories(await api("/categories/public").catch(() => [])); } catch {}
      try { if (user?.user_id) setStoreCategories(await api(`/sellers/${user.user_id}/categories`).catch(() => [])); } catch {}
      let ints = [];
      try { ints = await api("/interests"); setInterests(ints); } catch {}
      try { setCities(await api("/cities")); } catch {}
      if (isEdit) {
        try {
          const p = await api(`/products/${id}`);
          setName(p.name || ""); setDescription(p.description || ""); setPrice(String(p.price ?? ""));
          setQuantity(String(p.quantity ?? "1")); setDiscount(p.discount_percent ? String(p.discount_percent) : "");
          setImages(p.images_base64?.length ? p.images_base64 : (p.image_base64 ? [p.image_base64] : []));
          setCategoryId(p.category_id || ""); setStoreCategoryId(p.store_category_id || "");
          const iid = (p.interest_ids || [])[0] || "";
          setInterestId(iid);
          const found = ints.find((x) => x.interest_id === iid);
          if (found?.parent_id) setOpenParent(found.parent_id);
          setDeliveryType(p.delivery_type || ""); setFlatPrice(p.delivery_flat_price ? String(p.delivery_flat_price) : "");
          const cp = {}; (p.delivery_city_prices || []).forEach((x) => { cp[x.city_id] = String(x.price); }); setCityPrices(cp);
        } catch (e) { toast.error(e.message); }
      }
      setLoading(false);
    })();
  }, [id]); // eslint-disable-line

  const onImages = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files.slice(0, 10)) {
      try { const d = await fileToCompressedDataUrl(f, 1080, 0.6); setImages((prev) => [...prev, d]); } catch {}
    }
  };

  const save = async () => {
    if (!name.trim()) return toast.error("اسم المنتج مطلوب");
    if (!price || Number(price) <= 0) return toast.error("السعر مطلوب");
    if (!storeCategoryId) return toast.error("اختر قائمة من قوائم متجرك");
    if (!interestId) return toast.error("اختر القسم الفرعي المناسب للمنتج");
    if (!deliveryType) return toast.error("اختر طريقة التوصيل للمنتج");
    const body = {
      name: name.trim(), description: description.trim(), price: Number(price), quantity: Number(quantity) || 0,
      images_base64: images, image_base64: images[0] || null, category_id: categoryId || undefined,
      store_category_id: storeCategoryId, interest_ids: [interestId], discount_percent: discount ? Number(discount) : 0,
      delivery_type: deliveryType,
    };
    if (deliveryType === "flat") body.delivery_flat_price = Number(flatPrice) || 0;
    if (deliveryType === "per_city") body.delivery_city_prices = Object.entries(cityPrices).filter(([, v]) => v !== "").map(([city_id, price]) => ({ city_id, price: Number(price) || 0 }));
    setBusy(true);
    try {
      if (isEdit) { await api(`/products/${id}`, { method: "PUT", body: JSON.stringify(body) }); toast.success("تم تحديث المنتج"); }
      else { await api("/products", { method: "POST", body: JSON.stringify(body) }); toast.success("تمت إضافة المنتج"); }
      navigate("/");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  if (loading) return <Loader />;

  return (
    <div className="animate-fade-in-up max-w-2xl mx-auto">
      <PageHeader title={isEdit ? "تعديل المنتج" : "إضافة منتج جديد"} />

      <L>صور المنتج</L>
      <div className="flex gap-2 flex-wrap">
        {images.map((img, i) => (
          <div key={i} className="relative w-24 h-24 rounded-xl overflow-hidden">
            <img src={img} alt="" className="w-full h-full object-cover" />
            <button onClick={() => setImages((p) => p.filter((_, idx) => idx !== i))} className="absolute top-1 end-1 w-6 h-6 rounded-full bg-white flex items-center justify-center"><X size={14} className="text-[#FF3D5A]" /></button>
          </div>
        ))}
        <label className="w-24 h-24 rounded-xl border-2 border-dashed border-[#FF3D5A] bg-[#FFE5EA] flex flex-col items-center justify-center cursor-pointer" data-testid="add-images">
          <Upload size={22} className="text-[#FF3D5A]" /><span className="text-[10px] text-[#FF3D5A] font-bold mt-1">إضافة</span>
          <input type="file" accept="image/*" multiple className="hidden" onChange={onImages} />
        </label>
      </div>

      <L>اسم المنتج *</L>
      <In testId="product-name" v={name} on={setName} ph="مثال: قميص قطني" />
      <L>الوصف</L>
      <textarea data-testid="product-description" value={description} onChange={(e) => setDescription(e.target.value)} className="w-full bg-white rounded-xl px-4 py-3 border border-[#E8EAF2] outline-none focus:ring-2 focus:ring-[#FF3D5A] min-h-[90px]" />
      <div className="grid grid-cols-3 gap-3">
        <div><L>السعر (د.ل) *</L><In testId="product-price" v={price} on={setPrice} type="number" /></div>
        <div><L>الكمية</L><In testId="product-quantity" v={quantity} on={setQuantity} type="number" /></div>
        <div><L>خصم %</L><In testId="product-discount" v={discount} on={setDiscount} type="number" ph="0" /></div>
      </div>

      {storeCategories.length > 0 ? (
        <>
          <L>قائمة متجرك * (إلزامي)</L>
          <select data-testid="product-store-category" value={storeCategoryId} onChange={(e) => setStoreCategoryId(e.target.value)} className="w-full bg-white rounded-xl px-3 py-3 border border-[#E8EAF2] outline-none">
            <option value="">اختر قائمة من قوائم متجرك</option>
            {storeCategories.map((c) => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
          </select>
        </>
      ) : (
        <div className="mt-4 p-4 rounded-2xl bg-[#FFF7E0] border border-[#FFB627] text-[#B07700] text-sm font-bold">
          يجب إنشاء قائمة واحدة على الأقل في متجرك قبل إضافة منتج.
          <button data-testid="go-create-store-category" onClick={() => navigate("/store/categories")} className="block mt-2 text-[#FF3D5A] underline">إنشاء قوائم متجري الآن</button>
        </div>
      )}

      {categories.length > 0 && (
        <>
          <L>قائمة الإدارة (اختياري)</L>
          <select data-testid="product-category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="w-full bg-white rounded-xl px-3 py-3 border border-[#E8EAF2] outline-none">
            <option value="">بدون</option>
            {categories.map((c) => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
          </select>
        </>
      )}

      <L>القسم الفرعي للمنتج * (اختر قسمًا فرعيًا واحدًا فقط)</L>
      <p className="text-xs text-[#B07700] bg-[#FFF7E0] border border-[#FFB627] rounded-xl px-3 py-2 mb-2">📍 يرجى وضع المنتج في مكانه الصحيح</p>
      <div className="space-y-2">
        {interests.filter((i) => !i.parent_id).map((parent) => {
          const children = interests.filter((c) => c.parent_id === parent.interest_id);
          const isOpen = openParent === parent.interest_id;
          return (
            <div key={parent.interest_id} className="border border-[#E8EAF2] rounded-xl overflow-hidden">
              <button type="button" data-testid={`interest-parent-${parent.interest_id}`} onClick={() => setOpenParent(isOpen ? "" : parent.interest_id)} className="w-full flex items-center justify-between px-4 py-3 bg-white font-bold text-sm">
                <span>{parent.name}</span>
                <span className="text-[#9CA3AF]">{children.length ? (isOpen ? "▲" : "▼") : "—"}</span>
              </button>
              {isOpen && (
                <div className="flex gap-2 flex-wrap p-3 bg-[#F7F8FE]">
                  {children.length === 0 ? <span className="text-xs text-[#6B7280]">لا توجد أقسام فرعية</span> :
                    children.map((c) => (
                      <button key={c.interest_id} type="button" data-testid={`interest-${c.interest_id}`} onClick={() => setInterestId(c.interest_id)} className={`px-4 py-2 rounded-full border font-bold text-sm ${interestId === c.interest_id ? "bg-[#FF3D5A] border-[#FF3D5A] text-white" : "bg-white border-[#E8EAF2]"}`}>{c.name}</button>
                    ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <L>التوصيل * (إلزامي)</L>
      <div className="flex gap-2 flex-wrap">
        {[["free", "مجاني"], ["flat", "سعر موحّد"], ["per_city", "حسب المدينة"]].map(([k, l]) => (
          <button key={k} data-testid={`delivery-${k}`} onClick={() => setDeliveryType(k)} className={`px-4 py-2 rounded-xl border font-bold text-sm ${deliveryType === k ? "bg-[#FF3D5A] border-[#FF3D5A] text-white" : "bg-white border-[#E8EAF2]"}`}>{l}</button>
        ))}
      </div>
      {deliveryType === "flat" && <In testId="flat-price" v={flatPrice} on={setFlatPrice} type="number" ph="سعر التوصيل الموحّد" />}
      {deliveryType === "per_city" && (
        <div className="mt-2 space-y-2">
          {cities.map((c) => (
            <div key={c.city_id} className="flex items-center gap-2">
              <span className="flex-1 text-sm font-bold">{c.name}</span>
              <input type="number" value={cityPrices[c.city_id] ?? ""} onChange={(e) => setCityPrices((p) => ({ ...p, [c.city_id]: e.target.value }))} placeholder="السعر" className="w-28 bg-white rounded-xl px-3 py-2 border border-[#E8EAF2] outline-none" />
            </div>
          ))}
        </div>
      )}

      <button data-testid="save-product" disabled={busy} onClick={save} className="w-full h-14 rounded-2xl bg-[#FF3D5A] text-white font-black mt-6 disabled:opacity-60 flex items-center justify-center gap-2">
        <Plus size={20} /> {busy ? "..." : isEdit ? "حفظ التعديلات" : "نشر المنتج"}
      </button>
    </div>
  );
}
function L({ children }) { return <p className="text-sm font-bold text-[#161629] mt-4 mb-1.5">{children}</p>; }
function In({ v, on, ph, type, testId }) { return <input data-testid={testId} type={type || "text"} value={v} onChange={(e) => on(e.target.value)} placeholder={ph} className="w-full bg-white rounded-xl px-4 py-3 border border-[#E8EAF2] outline-none focus:ring-2 focus:ring-[#FF3D5A]" />; }
